import { BottomTabBarButtonProps } from "@react-navigation/bottom-tabs";
import { PlatformPressable } from "@react-navigation/elements";
import * as Haptics from "expo-haptics";
import { useTheme } from "@/hooks/useTheme";
import { useState, isValidElement, cloneElement, ReactElement, useContext, useRef } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { useToolTip } from "@/hooks/useToolTip";
import { RootLayoutContext } from "@/app/(app)/_layout";
import useAuth from "@/hooks/useAuth";
import { ThemedText } from "@/components/ThemedText";
import { titleCase } from "@/lib/helpers";

export function WebTab(props: BottomTabBarButtonProps) {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const toolTip = useToolTip();

  const ref = useRef<View>(null);
  const [hovered, setHovered] = useState(false);

  const rootLayoutCtx = useContext(RootLayoutContext);
  const isWeb: boolean = Platform.OS === "web";

  const InnerTab =
    isValidElement(props.children) &&
    cloneElement(props.children as ReactElement, {
      style: [styles.children],
    });

  return (
    <PlatformPressable
      ref={ref}
      id={`${props.href?.split("/")[1] ?? "dashboard"}-tab-button`}
      {...props}
      onHoverIn={() => {
        setHovered(true);
        if (rootLayoutCtx?.tabsCollapsed) {
          const rootName = props.href !== "/" ? props.href?.split("/")[1].split("?")[0] : "Dashboard";
          if (!rootName) return;
          const tabname = displayNames?.[rootName]?.plural ?? titleCase(rootName);
          toolTip.setToolTipContent(
            <View style={[{ backgroundColor: colors.background, borderColor: colors.border }, styles.tooltip]}>
              <ThemedText>{tabname}</ThemedText>
            </View>,
          );
          // Measure fresh on hover to get accurate position
          ref.current?.measureInWindow((_, pageY) => {
            toolTip.setLocation({ x: 55, y: pageY + 2 } as any);
            toolTip.setVisible(true);
          });
        }
      }}
      onHoverOut={() => {
        setHovered(false);
        toolTip.setVisible(false);
      }}
      style={[
        { position: "relative" },
        props.style,
        hovered && isWeb && { backgroundColor: colors.border },
        styles.container,
      ]}
      onPressIn={(ev: any) => {
        if (process.env.EXPO_OS === "ios") {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        }
        props.onPressIn?.(ev);
      }}
    >
      {InnerTab}
    </PlatformPressable>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
  children: {
    margin: 20,
  },
  tooltip: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
});
